import { useState, useRef, useCallback, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Copy, ClipboardPaste, Download, Save, AlertTriangle } from 'lucide-react';
import DependencyTracker from '../../utils/dependencyTracker';

interface Cell {
  value: string | number;
  formula?: string;
  format?: string;
  style?: React.CSSProperties;
}

interface ExcelGridProps {
  rows?: number;
  cols?: number;
  initialData?: Cell[][];
  onCellChange?: (row: number, col: number, value: string | number) => void;
  readOnly?: boolean;
}

const ExcelGrid = ({
  rows = 1000,
  cols = 26,
  initialData = [],
  onCellChange,
  readOnly = false,
}: ExcelGridProps) => {
  // Initialize grid data
  const [data, setData] = useState<Cell[][]>(() => {
    const grid: Cell[][] = [];
    for (let i = 0; i < rows; i++) {
      grid[i] = [];
      for (let j = 0; j < cols; j++) {
        grid[i][j] = initialData[i]?.[j] || { value: '' };
      }
    }
    return grid;
  });

  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [copiedCell, setCopiedCell] = useState<{ row: number; col: number } | null>(null);
  const [circularReferenceError, setCircularReferenceError] = useState<string | null>(null);
  const [dependentCells, setDependentCells] = useState<Set<string>>(new Set());

  const parentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dependencyTrackerRef = useRef<DependencyTracker>(new DependencyTracker());

  // Virtual scrolling for rows
  const rowVirtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32,
    overscan: 10,
  });

  // Convert column index to letter (A, B, C, ... Z, AA, AB, ...)
  const getColumnLetter = (col: number): string => {
    let letter = '';
    let num = col;
    while (num >= 0) {
      letter = String.fromCharCode(65 + (num % 26)) + letter;
      num = Math.floor(num / 26) - 1;
    }
    return letter;
  };

  // Handle cell click
  const handleCellClick = (row: number, col: number) => {
    setSelectedCell({ row, col });
    setEditingCell(null);
  };

  // Handle cell double click - enter edit mode
  const handleCellDoubleClick = (row: number, col: number) => {
    if (readOnly) return;

    setEditingCell({ row, col });
    const cell = data[row][col];
    setEditValue(cell.formula || String(cell.value));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Handle cell edit
  const handleCellEdit = (value: string) => {
    setEditValue(value);
  };

  // Handle cell edit complete
  const handleCellEditComplete = () => {
    if (editingCell) {
      const { row, col } = editingCell;
      const newData = [...data];

      // Check if it's a formula (starts with =)
      if (editValue.startsWith('=')) {
        // Add formula to dependency tracker
        dependencyTrackerRef.current.addFormula(row, col, editValue);

        // Check for circular references
        const circularRefs = dependencyTrackerRef.current.getCircularReferences();
        if (circularRefs.length > 0) {
          setCircularReferenceError(circularRefs[0].message);
          newData[row][col] = {
            ...newData[row][col],
            formula: editValue,
            value: '#CIRCULAR!',
          };
        } else {
          setCircularReferenceError(null);
          newData[row][col] = {
            ...newData[row][col],
            formula: editValue,
            value: evaluateFormula(editValue, newData),
          };
        }
      } else {
        // Remove formula from dependency tracker if it existed
        if (data[row][col].formula) {
          dependencyTrackerRef.current.removeFormula(row, col);
        }

        newData[row][col] = {
          ...newData[row][col],
          value: editValue,
          formula: undefined,
        };
      }

      // Recalculate all dependent cells
      recalculateDependentCells(row, col, newData);

      setData(newData);

      if (onCellChange) {
        onCellChange(row, col, editValue);
      }

      setEditingCell(null);
      setEditValue('');
    }
  };

  // Recalculate all cells that depend on the changed cell
  const recalculateDependentCells = (row: number, col: number, gridData: Cell[][]) => {
    const dependents = dependencyTrackerRef.current.getDependentCells(row, col);
    const calculationOrder = dependencyTrackerRef.current.getCalculationOrder();

    // Filter calculation order to only include dependent cells
    const dependentAddresses = new Set(dependents.map((d) => d.address));
    const cellsToRecalculate = calculationOrder.filter((cell) =>
      dependentAddresses.has(cell.address)
    );

    // Recalculate each dependent cell in the correct order
    cellsToRecalculate.forEach((cell) => {
      const cellData = gridData[cell.row][cell.col];
      if (cellData.formula) {
        gridData[cell.row][cell.col] = {
          ...cellData,
          value: evaluateFormula(cellData.formula, gridData),
        };
      }
    });
  };

  // Enhanced formula evaluation with advanced functions
  const evaluateFormula = (formula: string, gridData: Cell[][] = data): string | number => {
    try {
      // Remove the = sign
      const expression = formula.substring(1);

      // Handle SUM function
      if (expression.toUpperCase().startsWith('SUM(')) {
        const range = expression.match(/SUM\(([^)]+)\)/i)?.[1];
        if (range) {
          return evaluateSum(range, gridData);
        }
      }

      // Handle AVERAGE function
      if (expression.toUpperCase().startsWith('AVERAGE(')) {
        const range = expression.match(/AVERAGE\(([^)]+)\)/i)?.[1];
        if (range) {
          return evaluateAverage(range, gridData);
        }
      }

      // Handle COUNT function
      if (expression.toUpperCase().startsWith('COUNT(')) {
        const range = expression.match(/COUNT\(([^)]+)\)/i)?.[1];
        if (range) {
          return evaluateCount(range, gridData);
        }
      }

      // Handle MIN function
      if (expression.toUpperCase().startsWith('MIN(')) {
        const range = expression.match(/MIN\(([^)]+)\)/i)?.[1];
        if (range) {
          return evaluateMin(range, gridData);
        }
      }

      // Handle MAX function
      if (expression.toUpperCase().startsWith('MAX(')) {
        const range = expression.match(/MAX\(([^)]+)\)/i)?.[1];
        if (range) {
          return evaluateMax(range, gridData);
        }
      }

      // Handle IF function: =IF(condition, valueIfTrue, valueIfFalse)
      if (expression.toUpperCase().startsWith('IF(')) {
        const ifMatch = expression.match(/IF\(([^,]+),([^,]+),([^)]+)\)/i);
        if (ifMatch) {
          return evaluateIf(ifMatch[1].trim(), ifMatch[2].trim(), ifMatch[3].trim(), gridData);
        }
      }

      // Handle simple cell reference (e.g., =A1)
      const cellRef = expression.match(/^([A-Z]+)(\d+)$/i);
      if (cellRef) {
        const col = cellRef[1].charCodeAt(0) - 65;
        const row = parseInt(cellRef[2]) - 1;
        return gridData[row]?.[col]?.value || 0;
      }

      // Try to evaluate as math expression
      // eslint-disable-next-line no-eval
      return eval(expression);
    } catch (error) {
      return '#ERROR';
    }
  };

  const evaluateSum = (range: string, gridData: Cell[][]): number => {
    const cells = parseRange(range);
    let sum = 0;
    cells.forEach(({ row, col }) => {
      const value = gridData[row]?.[col]?.value;
      if (typeof value === 'number') {
        sum += value;
      } else if (typeof value === 'string' && !isNaN(Number(value))) {
        sum += Number(value);
      }
    });
    return sum;
  };

  const evaluateAverage = (range: string, gridData: Cell[][]): number => {
    const cells = parseRange(range);
    const sum = evaluateSum(range, gridData);
    return cells.length > 0 ? sum / cells.length : 0;
  };

  const evaluateCount = (range: string, gridData: Cell[][]): number => {
    const cells = parseRange(range);
    let count = 0;
    cells.forEach(({ row, col }) => {
      const value = gridData[row]?.[col]?.value;
      if (value !== undefined && value !== null && value !== '') {
        count++;
      }
    });
    return count;
  };

  const evaluateMin = (range: string, gridData: Cell[][]): number => {
    const cells = parseRange(range);
    const values: number[] = [];

    cells.forEach(({ row, col }) => {
      const value = gridData[row]?.[col]?.value;
      if (typeof value === 'number') {
        values.push(value);
      } else if (typeof value === 'string' && !isNaN(Number(value))) {
        values.push(Number(value));
      }
    });

    return values.length > 0 ? Math.min(...values) : 0;
  };

  const evaluateMax = (range: string, gridData: Cell[][]): number => {
    const cells = parseRange(range);
    const values: number[] = [];

    cells.forEach(({ row, col }) => {
      const value = gridData[row]?.[col]?.value;
      if (typeof value === 'number') {
        values.push(value);
      } else if (typeof value === 'string' && !isNaN(Number(value))) {
        values.push(Number(value));
      }
    });

    return values.length > 0 ? Math.max(...values) : 0;
  };

  const evaluateIf = (
    condition: string,
    valueIfTrue: string,
    valueIfFalse: string,
    gridData: Cell[][]
  ): string | number => {
    try {
      // Replace cell references in condition
      let processedCondition = condition;
      const cellRefs = condition.match(/([A-Z]+)(\d+)/gi);

      if (cellRefs) {
        cellRefs.forEach((ref) => {
          const match = ref.match(/([A-Z]+)(\d+)/i);
          if (match) {
            const col = match[1].charCodeAt(0) - 65;
            const row = parseInt(match[2]) - 1;
            const value = gridData[row]?.[col]?.value || 0;
            processedCondition = processedCondition.replace(ref, String(value));
          }
        });
      }

      // Evaluate condition
      // eslint-disable-next-line no-eval
      const conditionResult = eval(processedCondition);

      // Return appropriate value
      if (conditionResult) {
        // Check if valueIfTrue is a cell reference
        const cellRef = valueIfTrue.match(/^([A-Z]+)(\d+)$/i);
        if (cellRef) {
          const col = cellRef[1].charCodeAt(0) - 65;
          const row = parseInt(cellRef[2]) - 1;
          return gridData[row]?.[col]?.value || 0;
        }
        // Check if it's a number
        if (!isNaN(Number(valueIfTrue))) {
          return Number(valueIfTrue);
        }
        // Return as string (remove quotes if present)
        return valueIfTrue.replace(/^["']|["']$/g, '');
      } else {
        // Check if valueIfFalse is a cell reference
        const cellRef = valueIfFalse.match(/^([A-Z]+)(\d+)$/i);
        if (cellRef) {
          const col = cellRef[1].charCodeAt(0) - 65;
          const row = parseInt(cellRef[2]) - 1;
          return gridData[row]?.[col]?.value || 0;
        }
        // Check if it's a number
        if (!isNaN(Number(valueIfFalse))) {
          return Number(valueIfFalse);
        }
        // Return as string (remove quotes if present)
        return valueIfFalse.replace(/^["']|["']$/g, '');
      }
    } catch (error) {
      return '#ERROR';
    }
  };

  const parseRange = (range: string): Array<{ row: number; col: number }> => {
    const cells: Array<{ row: number; col: number }> = [];

    // Handle range like A1:A10
    const rangeMatch = range.match(/([A-Z]+)(\d+):([A-Z]+)(\d+)/i);
    if (rangeMatch) {
      const startCol = rangeMatch[1].charCodeAt(0) - 65;
      const startRow = parseInt(rangeMatch[2]) - 1;
      const endCol = rangeMatch[3].charCodeAt(0) - 65;
      const endRow = parseInt(rangeMatch[4]) - 1;

      for (let row = startRow; row <= endRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          cells.push({ row, col });
        }
      }
    }

    return cells;
  };

  // Update dependent cells when selection changes
  useEffect(() => {
    if (selectedCell) {
      const dependents = dependencyTrackerRef.current.getDependentCells(
        selectedCell.row,
        selectedCell.col
      );
      const dependentAddresses = new Set(dependents.map((d) => d.address));
      setDependentCells(dependentAddresses);
    } else {
      setDependentCells(new Set());
    }
  }, [selectedCell]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedCell) return;

      const { row, col } = selectedCell;

      // Prevent default for arrow keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab'].includes(e.key)) {
        e.preventDefault();
      }

      if (editingCell) {
        // In edit mode
        if (e.key === 'Enter' || e.key === 'Tab') {
          handleCellEditComplete();

          if (e.key === 'Enter') {
            setSelectedCell({ row: Math.min(row + 1, rows - 1), col });
          } else if (e.key === 'Tab') {
            setSelectedCell({ row, col: Math.min(col + 1, cols - 1) });
          }
        } else if (e.key === 'Escape') {
          setEditingCell(null);
          setEditValue('');
        }
      } else {
        // Navigation mode
        switch (e.key) {
          case 'ArrowUp':
            if (row > 0) setSelectedCell({ row: row - 1, col });
            break;
          case 'ArrowDown':
            if (row < rows - 1) setSelectedCell({ row: row + 1, col });
            break;
          case 'ArrowLeft':
            if (col > 0) setSelectedCell({ row, col: col - 1 });
            break;
          case 'ArrowRight':
            if (col < cols - 1) setSelectedCell({ row, col: col + 1 });
            break;
          case 'Enter':
            handleCellDoubleClick(row, col);
            break;
          case 'Tab':
            setSelectedCell({ row, col: Math.min(col + 1, cols - 1) });
            break;
          case 'Delete':
          case 'Backspace':
            if (!readOnly) {
              const newData = [...data];
              newData[row][col] = { value: '' };
              setData(newData);
            }
            break;
          // Copy (Ctrl+C)
          case 'c':
            if (e.ctrlKey || e.metaKey) {
              setCopiedCell({ row, col });
            }
            break;
          // Paste (Ctrl+V)
          case 'v':
            if ((e.ctrlKey || e.metaKey) && copiedCell) {
              if (!readOnly) {
                const newData = [...data];
                newData[row][col] = { ...data[copiedCell.row][copiedCell.col] };
                setData(newData);
              }
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell, editingCell, editValue, copiedCell, data, rows, cols, readOnly]);

  // Format cell value for display
  const formatCellValue = (cell: Cell): string => {
    if (cell.value === '') return '';
    if (typeof cell.value === 'number') {
      return cell.format === 'currency'
        ? `$${cell.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : cell.value.toLocaleString();
    }
    return String(cell.value);
  };

  return (
    <div className="excel-grid-container">
      {/* Circular Reference Error Banner */}
      {circularReferenceError && (
        <div className="bg-alert-50 border-l-4 border-alert-500 p-3 mb-2">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-alert-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-alert-800">Circular Reference Detected</p>
              <p className="text-xs text-alert-700 mt-1">{circularReferenceError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 bg-white border-b border-neutral-200">
        <div className="flex items-center space-x-2">
          <button className="p-2 hover:bg-neutral-100 rounded" title="Copy (Ctrl+C)">
            <Copy className="w-4 h-4" />
          </button>
          <button className="p-2 hover:bg-neutral-100 rounded" title="Paste (Ctrl+V)">
            <ClipboardPaste className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-neutral-300 mx-2" />
          <button className="p-2 hover:bg-neutral-100 rounded" title="Save">
            <Save className="w-4 h-4" />
          </button>
          <button className="p-2 hover:bg-neutral-100 rounded" title="Export">
            <Download className="w-4 h-4" />
          </button>
        </div>

        {selectedCell && (
          <div className="text-sm text-neutral-600">
            Cell: {getColumnLetter(selectedCell.col)}{selectedCell.row + 1}
          </div>
        )}
      </div>

      {/* Formula Bar */}
      <div className="p-2 bg-white border-b border-neutral-200">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-neutral-700 w-16">
            {selectedCell ? `${getColumnLetter(selectedCell.col)}${selectedCell.row + 1}` : ''}
          </span>
          <input
            type="text"
            className="flex-1 px-3 py-1 border border-neutral-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={
              selectedCell
                ? data[selectedCell.row][selectedCell.col].formula ||
                  String(data[selectedCell.row][selectedCell.col].value)
                : ''
            }
            readOnly={!selectedCell || readOnly}
            onChange={(e) => {
              if (selectedCell && !readOnly) {
                const newData = [...data];
                const value = e.target.value;
                if (value.startsWith('=')) {
                  newData[selectedCell.row][selectedCell.col] = {
                    ...newData[selectedCell.row][selectedCell.col],
                    formula: value,
                    value: evaluateFormula(value),
                  };
                } else {
                  newData[selectedCell.row][selectedCell.col] = {
                    ...newData[selectedCell.row][selectedCell.col],
                    value,
                    formula: undefined,
                  };
                }
                setData(newData);
              }
            }}
            placeholder={'Enter value or formula (e.g. =SUM(A1:A10), =IF(A1>100,"High","Low"), =COUNT(A1:A10))'}
          />
        </div>
      </div>

      {/* Grid */}
      <div
        ref={parentRef}
        className="overflow-auto h-[600px] border border-neutral-300 bg-white"
      >
        {/* Column headers */}
        <div className="sticky top-0 z-20 flex bg-neutral-100 border-b border-neutral-300">
          <div className="w-12 h-8 flex items-center justify-center border-r border-neutral-300 bg-neutral-200 font-semibold text-xs" />
          {Array.from({ length: cols }).map((_, col) => (
            <div
              key={col}
              className="min-w-[120px] h-8 flex items-center justify-center border-r border-neutral-300 font-semibold text-xs text-neutral-700"
            >
              {getColumnLetter(col)}
            </div>
          ))}
        </div>

        {/* Rows */}
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = virtualRow.index;
            return (
              <div
                key={row}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className="flex"
              >
                {/* Row number */}
                <div className="w-12 flex items-center justify-center bg-neutral-100 border-r border-b border-neutral-300 font-semibold text-xs text-neutral-700">
                  {row + 1}
                </div>

                {/* Cells */}
                {Array.from({ length: cols }).map((_, col) => {
                  const cell = data[row][col];
                  const isSelected = selectedCell?.row === row && selectedCell?.col === col;
                  const isEditing = editingCell?.row === row && editingCell?.col === col;
                  const isCopied = copiedCell?.row === row && copiedCell?.col === col;

                  // Check if this cell is a dependent of the selected cell
                  const columnLetter = getColumnLetter(col);
                  const cellAddress = `${columnLetter}${row + 1}`;
                  const isDependent = dependentCells.has(cellAddress);

                  return (
                    <div
                      key={col}
                      className={`min-w-[120px] px-2 border-r border-b border-neutral-200 flex items-center cursor-cell ${
                        isSelected ? 'bg-primary-100 border-2 border-primary-500 z-10' : ''
                      } ${isDependent ? 'bg-success-50 border-success-300' : ''} ${
                        isCopied ? 'border-2 border-dashed border-success-500' : ''
                      } ${
                        !isEditing && !readOnly && !isDependent ? 'hover:bg-primary-50' : ''
                      }`}
                      onClick={() => handleCellClick(row, col)}
                      onDoubleClick={() => handleCellDoubleClick(row, col)}
                    >
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          type="text"
                          className="w-full bg-transparent outline-none font-mono text-sm"
                          value={editValue}
                          onChange={(e) => handleCellEdit(e.target.value)}
                          onBlur={handleCellEditComplete}
                        />
                      ) : (
                        <span className="text-sm text-neutral-900 font-mono truncate">
                          {formatCellValue(cell)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-neutral-50 border-t border-neutral-200 text-xs text-neutral-600">
        <div>
          {rows.toLocaleString()} rows × {cols} columns
        </div>
        <div className="flex items-center space-x-4">
          <span>Zoom: 100%</span>
          <span>Ready</span>
        </div>
      </div>
    </div>
  );
};

export default ExcelGrid;
